/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftProgress from "components/SoftProgress";

// ProgressLineChart configurations
import configs from "examples/Charts/LineCharts/ProgressLineChart/config";

function ProgressLineChart({ color, icon, title, company, count, progressdesktop, progressmobile, height, chart }) {
  const { data, options } = configs(color, chart.labels || [], title, chart.data || []);

  return (
    <Card>
      <SoftBox display="flex" alignItems="top" pt={2} px={2}>
        <SoftBox
          width="2rem"
          height="2rem"
          display="grid"
          justifyContent="center"
          alignItems="center"
          borderRadius="md"
          shadow="md"
          color="white"
          bgColor={color}
          variant="gradient"
        >
          <Icon fontSize="default">{icon}</Icon>
        </SoftBox>
        <SoftBox ml={2} lineHeight={1}>
          <SoftTypography
            variant="button"
            fontWeight="bold"
            color="text"
          >
            {title}<br />
          </SoftTypography>
          <SoftTypography
            variant="button"
            fontWeight="medium"
            color="text"
          >
            {company}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      
      <SoftBox ml={2} mr={2}>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={4} lg={4}>
            {count ? (
              <SoftBox>
                <SoftTypography display="block" variant="caption" fontWeight="bold" color="text">
                  Seitenaufrufe
                </SoftTypography>
                <SoftTypography variant="h5" fontWeight="bold" mb={0}>
                  {count}
                </SoftTypography>
              </SoftBox>
            ) : null}
          </Grid>
          <Grid item xs={4} lg={4}>
            <SoftBox>
              <SoftBox ml="auto">
                <SoftTypography display="block" variant="caption" fontWeight="bold" color="text">
                  Desktop
                </SoftTypography>
                <SoftTypography display="block" variant="caption" fontWeight="medium" color="text">
                  {progressdesktop}%
                </SoftTypography>
                <SoftBox mt={0.25}>
                  <SoftProgress variant="gradient" color={color} value={progressdesktop} />
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Grid>
          <Grid item xs={4} lg={4}>
            <SoftBox>
              <SoftBox ml="auto">
                <SoftTypography display="block" variant="caption" fontWeight="bold" color="text">
                  Mobil
                </SoftTypography>
                <SoftTypography display="block" variant="caption" fontWeight="medium" color="text">
                  {progressmobile}%
                </SoftTypography>
                <SoftBox mt={0.25}>
                  <SoftProgress variant="gradient" color={color} value={progressmobile} />
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
      
      {useMemo(
        () => (
          <SoftBox mt={2}>
            <Line data={data} options={options} style={{ height }} />
          </SoftBox>
        ),
        [chart, height, color]
      )}
    </Card>
  );
}

// Setting default values for the props of ProgressLineChart
ProgressLineChart.defaultProps = {
  color: "info",
  count: 0,
  height: "6.25rem",
};

// Typechecking props for the ProgressLineChart
ProgressLineChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  // title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  progress: PropTypes.number.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
};

export default ProgressLineChart;
